import { ReactElement, useEffect, useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import i18next from 'i18next';

import GuestsClipboard from './GuestsClipboard';
import Book from './Book';
import { GuestsClipboardData } from '../../data/GuestsClipboardData';
import { ComposerBookDataDe, ComposerBookDataEn } from '../../data/ComposerBookData';
import Clipboard from '../../assets/puzzle6/clipboard.png';
import { Next } from '../icons/Icons';
import { Previous } from '../icons/Icons';
import { Puzzle } from '../../types/Puzzle.d';

type Props = {
  variant: Puzzle.FOUR | Puzzle.SIX;
};

export default function BookSlider(props: Props): ReactElement {
  const { variant } = props;
  const [currentPage, setCurrentPage] = useState<number>(0);
  const locale = i18next.resolvedLanguage.trim().toLowerCase().slice(0, 2);

  const [bookImage, setBookImage] = useState(ComposerBookDataDe);

  let length: number;
  let rightPos: number;

  if (variant === Puzzle.FOUR) {
    length = ComposerBookDataDe.length;
  } else {
    length = GuestsClipboardData.length + 1;
  }

  const isFirstPage = currentPage === 0;
  const isLastPage = currentPage === length - 1;

  const nextPage = (): void => {
    setCurrentPage(isLastPage ? 0 : currentPage + 1);
  };

  const prevPage = (): void => {
    setCurrentPage(isFirstPage ? length : currentPage - 1);
  };

  const getControlsPosition = (puzzleVariant: Puzzle.FOUR | Puzzle.SIX, controlsVariant: 'prev' | 'next'): number => {
    if (puzzleVariant === Puzzle.FOUR) {
      if (controlsVariant === 'next') rightPos = -3.8;
      else rightPos = 31;
    } else {
      if (controlsVariant === 'next') rightPos = -4.5;
      else rightPos = 32;
    }
    return rightPos;
  };

  useEffect(() => {
    switch (locale) {
      case 'de':
        setBookImage(ComposerBookDataDe);
        break;
      case 'en':
        setBookImage(ComposerBookDataEn);
        break;
    }
  }, [locale]);

  return (
    <StyledGuestListContainer>
      <StyledGuestListContent variant={variant}>
        <AnimatePresence>
          <motion.div
            key={currentPage}
            initial={{ opacity: 0, position: 'relative' }}
            animate={{ opacity: 1, position: 'relative' }}
            exit={{ opacity: 0, position: 'absolute' }}
          >
            {variant === Puzzle.FOUR ? (
              <Book currentPage={currentPage}  bookImage={bookImage} />
            ) : (
              <GuestsClipboard isFirstPage={isFirstPage} currentPage={currentPage} />
            )}
          </motion.div>
        </AnimatePresence>
        {currentPage >= 0 && !isLastPage && (
          <StyledPageControlButton rightPos={getControlsPosition(variant, 'next')}>
            <Next onClick={nextPage} />
          </StyledPageControlButton>
        )}
        {currentPage < length && !isFirstPage && (
          <StyledPageControlButton rightPos={getControlsPosition(variant, 'prev')}>
            <Previous onClick={prevPage} />
          </StyledPageControlButton>
        )}
      </StyledGuestListContent>
    </StyledGuestListContainer>
  );
}

const StyledGuestListContainer = styled.div`
  position: absolute;
  bottom: 10rem;
  width: 100%;
  text-align: center;
  height: 10rem;
  z-index: 4;
  margin: 0 auto;
  padding: 0;
`;

const StyledGuestListContent = styled.div<{ variant: Puzzle.FOUR | Puzzle.SIX }>`
  width: 37rem;
  height: ${({ variant }) => (variant === Puzzle.FOUR ? 'auto' : '20rem')};
  margin: 0 auto;
  position: relative;
  background: ${({ variant }) => (variant === Puzzle.FOUR ? `` : ` url('${Clipboard}')`)};
  background-position: center;
`;

const StyledPageControlButton = styled.div<{ rightPos: number }>`
  position: absolute;
  top: 12rem;
  right: ${({ rightPos }) => `${rightPos}rem`};
  transform: translate(-50%, -50%);
  cursor: pointer;
  svg {
    fill: ${({ theme }) => theme.palette.company.primary};
    transition: fill 0.3s ease-in;
    filter: drop-shadow(1px 1px 6px rgba(0, 0, 0, 0.45));
    :hover {
      fill: ${({ theme }) => theme.palette.company.hover.primary};
    }
  }
`;
